import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  UserGroupIcon, 
  ClockIcon,
  ArrowRightIcon,
  TvIcon,
  CalendarIcon,
  SpeakerWaveIcon
} from '@heroicons/react/24/outline';
import { apiService, Channel, Show, Episode } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Breadcrumb from '../components/Breadcrumb';
import { useAudio } from '../contexts/AudioContext';

const ShowDetail: React.FC = () => {
  const { channelId, showId } = useParams<{ channelId: string; showId: string }>();
  const navigate = useNavigate();
  const { startSession } = useAudio();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [show, setShow] = useState<Show | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingSession, setStartingSession] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!channelId || !showId) return;
      
      try {
        const [channelData, showData, episodesData] = await Promise.all([
          apiService.getChannel(channelId),
          apiService.getShow(showId),
          apiService.getEpisodes()
        ]);
        
        setChannel(channelData);
        setShow(showData);
        setEpisodes(episodesData.filter(episode => episode.showId === showId));
      } catch (error) {
        console.error('Error fetching show data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [channelId, showId]);

  const handleStartSession = async (episodeId: string, episodeTitle: string) => {
    try {
      setStartingSession(episodeId);
      await startSession(episodeId, episodeTitle);
      // Audio will start playing automatically via the floating player
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setStartingSession(null);
    }
  };

  const sortedEpisodes = episodes.sort((a, b) => 
    new Date(b.publishedAt || b.createdAt).getTime() - 
    new Date(a.publishedAt || a.createdAt).getTime()
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!channel || !show) {
    return (
      <div className="text-center py-12">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Show Not Found
        </h3>
        <button
          onClick={() => navigate('/channels')}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300"
        >
          <ArrowRightIcon className="w-5 h-5" />
          <span>Back to Channels</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={[
          { label: 'Channels', href: '/channels' },
          { label: channel.name, href: `/channels/${channelId}` },
          { label: show.name, isCurrent: true }
        ]} 
      />

      {/* Show Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            {show.logoUrl ? (
              <img 
                src={show.logoUrl} 
                alt={show.name}
                className="w-20 h-20 rounded-lg object-cover"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <SpeakerWaveIcon className="w-10 h-10 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {show.name}
              </h1>
              {show.description && (
                <p className="text-lg text-gray-600 dark:text-gray-400 mt-2 max-w-3xl">
                  {show.description}
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              show.status === 'active' 
                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}>
              {show.status}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <UserGroupIcon className="w-5 h-5" />
            <span>{show.panelistIds.length} panelist{show.panelistIds.length !== 1 ? 's' : ''}</span>
          </div>
          
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <ClockIcon className="w-5 h-5" />
            <span>{show.frequency}</span>
          </div>

          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <TvIcon className="w-5 h-5" />
            <span>{episodes.length} episode{episodes.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {show.category && (
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
              {show.category}
            </span>
            {show.tags && show.tags.length > 0 && show.tags.slice(0, 5).map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Episodes */}
      {episodes.length > 0 ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Episodes
            </h2>
            <span className="text-gray-600 dark:text-gray-400">
              {episodes.length} episode{episodes.length !== 1 ? 's' : ''}
            </span>
          </div>
          
          <div className="space-y-4">
            {sortedEpisodes.map((episode) => (
              <div
                key={episode._id}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {episode.title}
                    </h3>
                    {episode.description && (
                      <p className="text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                        {episode.description}
                      </p>
                    )}
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <CalendarIcon className="w-4 h-4" />
                        <span>
                          {new Date(episode.publishedAt || episode.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      
                      {episode.duration && (
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{Math.round(episode.duration / 60)} min</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-6 flex items-center space-x-3">
                    <button
                      onClick={() => handleStartSession(episode._id, episode.title)}
                      disabled={startingSession === episode._id}
                      className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
                    >
                      {startingSession === episode._id ? (
                        <>
                          <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          <span>Starting...</span>
                        </>
                      ) : (
                        <>
                          <PlayIcon className="w-5 h-5" />
                          <span>Play Episode</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => navigate(`/channels/${channelId}/shows/${showId}/episodes/${episode._id}`)}
                      className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 font-medium flex items-center space-x-1 py-3 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span>Details</span>
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <SpeakerWaveIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Episodes Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            This show doesn't have any episodes yet. Check back soon!
          </p>
        </div>
      )}
    </div>
  );
};

export default ShowDetail;
